import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { useSEO } from "@/hooks/useSEO";

export default function ForgotPassword() {
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  useSEO({
    title: lang === "es"
      ? "Recuperar contraseña — ChefSync P&L"
      : "Recover password — ChefSync P&L",
    description: lang === "es"
      ? "¿Olvidaste tu contraseña de ChefSync P&L? Recupera el acceso a tu panel de facturas, gastos e ingresos en segundos."
      : "Forgot your ChefSync P&L password? Regain access to your invoices, expenses and income dashboard in seconds.",
    // Recovery pages shouldn't compete with landing pages for rankings.
    noIndex: true,
  });
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data.reset_token) {
        setToken(data.reset_token);
        toast.success(lang === "es" ? "Token generado" : "Token generated");
      } else {
        toast.success(lang === "es" ? "Si el email existe, recibirás un token" : "If the email exists, you'll get a token");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[var(--background)]">
      <div className="w-full max-w-md bg-[var(--surface)] border border-[var(--border-color)] rounded-lg p-8">
        <div className="flex items-center justify-between mb-8">
          <Link to="/login" className="flex items-center gap-2">
            <Logo size={32} />
            <span className="font-display font-bold">ChefSync <span className="text-[var(--text-muted)] font-normal">P&amp;L</span></span>
          </Link>
          <button
            onClick={() => setLang(lang === "es" ? "en" : "es")}
            className="text-xs uppercase tracking-wider text-[var(--text-secondary)]"
          >
            {lang === "es" ? "EN" : "ES"}
          </button>
        </div>

        <p className="text-xs uppercase tracking-[0.15em] text-[var(--text-muted)] mb-2">
          {t("resetTitle")}
        </p>
        <h1 className="font-display text-3xl font-bold tracking-tight mb-6">{t("forgotPassword")}</h1>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">{t("email")}</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="forgot-email-input"
              className="h-11"
              placeholder="you@email.com"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="forgot-submit"
            className="w-full h-11 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
          >
            {loading ? "..." : t("sendReset")}
          </Button>
        </form>

        {token && (
          <div className="mt-6 p-4 rounded-md bg-[var(--status-warning-bg)] border border-[#f3d59b]">
            <p className="text-xs uppercase tracking-wider text-[var(--status-warning)] mb-1">
              {lang === "es" ? "Token de recuperación (modo dev)" : "Reset token (dev mode)"}
            </p>
            <p className="font-mono text-xs break-all" data-testid="reset-token-output">{token}</p>
            <Button
              size="sm"
              variant="outline"
              className="mt-3 w-full"
              onClick={() => navigate(`/reset-password?token=${encodeURIComponent(token)}`)}
              data-testid="goto-reset-btn"
            >
              {t("resetTitle")} →
            </Button>
          </div>
        )}

        <p className="mt-6 text-sm text-[var(--text-secondary)] text-center">
          <Link to="/login" className="text-[var(--primary)] font-medium hover:underline">
            ← {t("login")}
          </Link>
        </p>
      </div>
    </div>
  );
}
